/**
 * useFeedbackGate.js
 * ─────────────────────────────────────────────────────────────────────────
 * Custom React hook that:
 *   1. Fetches /getscores from the main backend
 *   2. Checks if the user already has an "eventFeedback" or "networkFeedback"
 *      entry in their leaderboard scores array (gname field)
 *   3. Returns state used to show the golden RPG notification gate
 *
 * gname values to detect (exact match, case-insensitive):
 *   "eventFeedback"   → feedback already submitted for event type
 *   "networkFeedback" → feedback already submitted for network type
 * ─────────────────────────────────────────────────────────────────────────
 */

import { useState, useEffect } from "react"
import { isMobile } from "react-device-detect"

/**
 * @param {string} feedType  - "event" | "network"
 * @returns {{
 *   gateStatus: "loading" | "rules" | "played",
 *   quizScore:  number,        // points earned from this feedback (if played)
 *   dismiss:    Function,      // call to hide the gate (accepted or closed)
 * }}
 */
const useFeedbackGate = (feedType) => {
  const [gateStatus, setGateStatus] = useState("loading")
  const [quizScore, setQuizScore]   = useState(0)

  useEffect(() => {
    if (!feedType) return

    const run = async () => {
      try {
        const adminPanel = isMobile
          ? process.env.REACT_APP_ADMIN
          : process.env.REACT_APP_ADMIN_LAPTOP

        // Get current user email from localStorage
        const authUser = JSON.parse(localStorage.getItem("authUser") || "{}")
        const email    = authUser?.EmailAddress || authUser?.email || ""

        if (!email || !adminPanel) {
          // Can't verify — allow through (fail-open)
          setGateStatus("rules")
          return
        }

        // Fetch full leaderboard
        const res  = await fetch(`${adminPanel}/getscores`)
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const all  = await res.json()

        // Find this user's entry
        const userEntry = Array.isArray(all)
          ? all.find(u =>
              (u.id    && u.id.toLowerCase()    === email.toLowerCase()) ||
              (u.email && u.email.toLowerCase() === email.toLowerCase())
            )
          : null

        if (!userEntry) {
          setGateStatus("rules")
          return
        }

        // The gname to look for depends on feed type
        // DB stores: "eventFeedback" or "networkFeedback"
        const targetGname = feedType === "network" ? "networkfeedback" : "eventfeedback"

        const feedbackEntry = (userEntry.scores || []).find(
          s => s.gname && s.gname.trim().toLowerCase() === targetGname
        )

        if (feedbackEntry) {
          // Already submitted
          setQuizScore(Number(feedbackEntry.score) || 0)
          setGateStatus("played")
        } else {
          setGateStatus("rules")
        }
      } catch (err) {
        console.warn("[useFeedbackGate] Could not fetch leaderboard:", err.message)
        // Fail-open: let the user proceed
        setGateStatus("rules")
      }
    }

    run()
  }, [feedType])

  const dismiss = () => setGateStatus("dismissed")

  return { gateStatus, quizScore, dismiss }
}

export default useFeedbackGate